import { type ComponentType } from 'react';
import {
  Activity,
  FileText,
  FileUp,
  Waypoints,
  ArrowUpDown,
  PhoneOutgoing,
  Award,
  BarChart3,
  BookOpenCheck,
  Boxes,
  Building2,
  Calculator,
  CalendarClock,
  CalendarDays,
  Contact,
  Coins,
  Compass,
  FolderTree,
  Footprints,
  Globe,
  Heart,
  History,
  Landmark,
  Lightbulb,
  Link2,
  Map,
  Megaphone,
  MessageSquare,
  MessagesSquare,
  Columns3,
  MessageSquareText,
  Headphones,
  PhoneCall,
  Plug,
  Radar,
  Server,
  ShieldCheck,
  DoorOpen,
  Sun,
  Tags,
  Target,
  TrendingUp,
  Unlink,
  UserCog,
  Users,
  Wallet,
  MousePointerClick,
  ShoppingBag,
  Layers,
  Workflow,
  Eye,
  Bot,
  LifeBuoy,
} from 'lucide-react';

/**
 * ⛔ МЕНЮ АДМИНКИ — ЕДИНСТВЕННЫЙ ИСТОЧНИК ПРАВДЫ О СОСТАВЕ РАЗДЕЛОВ.
 *
 * Вынесено из `Sidebar.tsx` отдельным файлом (`prv3`): состав меню — это ДАННЫЕ, а не
 * часть компонента, и слой видимости разделов читает их наравне с самим меню. Держать
 * данные внутри компонента значило бы либо экспортировать из него не-компоненты (и
 * ломать горячую пересборку), либо завести вторую копию списка — а вторая копия
 * разъехалась бы с меню молча.
 *
 * ⛔ Серверный реестр `RazdelyAdminki` сверяется с ЭТИМ файлом сторожем
 * `ReestrRazdelovTest` при каждом прогоне — требование владельца «реестр = фактическое
 * меню, один в один». Правишь пункт здесь — правь и там, иначе прогон покраснеет.
 */
export type IconCmp = ComponentType<{ size?: number; className?: string }>;

export interface NavLeaf {
  /** Ключ раздела — им же слой видимости (`prv3`) прячет пункт. */
  key: string;
  to: string;
  label: string;
  permission?: string;
  /** Пункт только для владельца (super_admin). */
  superAdmin?: boolean;
}

export interface NavSection {
  /** Стабильный ключ для состояния «раскрыто/свёрнуто» и подсветки. */
  key: string;
  /** Собственная страница раздела (заголовок раздела ведёт на неё). */
  to: string;
  label: string;
  icon: IconCmp;
  /** Право на видимость самого раздела. */
  permission?: string;
  /** Раздел только для владельца (super_admin) — напр. Пульс (выгодность/деньги). */
  superAdmin?: boolean;
  /** Спрятать от конкретных ролей (напр. «Стол админа» не для директолога, 12.07). */
  hiddenForRoles?: string[];
  /**
   * Раздел ВИДЕН, но домом человеку не служит (`s303`).
   *
   * ⛔ ЗАЧЕМ ОТДЕЛЬНЫЙ ПРИЗНАК, А НЕ ПРАВО. Прототип экранов записи открыт всем
   * по решению штаба — права записи придут отдельной нитью доступов. Но «открыт»
   * и «годится стартовым экраном» — разные вещи: без этого признака первый же
   * раздел без права стал бы домом для всякого, у кого закрыто остальное, и
   * человек, которому не открыто НИЧЕГО, попадал бы в прототип вместо честного
   * «вам пока не открыт ни один раздел» (закон `prv4`).
   */
  neDom?: boolean;
  /**
   * Подзаголовок-принадлежность ВНУТРИ раздела (`s310`, владелец 09.09.2026:
   * «настройки разделить внутри по принадлежности — сейчас каша, непонятно
   * к чему какой пункт»). Соседние разделы с одинаковой подгруппой рисуются
   * под одним подзаголовком; сам подзаголовок — не ссылка и не пункт.
   */
  podgruppa?: string;
  children?: NavLeaf[];
}

/**
 * Раздел, внутри которого под выпадашкой ЗАФИКСИРОВАН месячный календарик
 * (владелец, 09.09.2026, приёмка по скрину, пункты 1–2): отдельного пункта
 * «Календарь» больше НЕТ — «слово календарь убираем, дублирует клик по любой
 * дате»; сам календарик стоит строго под выпадающим списком и никуда не
 * съезжает. Клик по дате открывает журнал этого дня (`/zapis/den?date=…`).
 * Ключ `kalendar` снят и из серверного реестра — пункта нет, прятать нечего.
 */
export const GRUPPA_S_KALENDAREM = 'Операционка';

/**
 * Ключ пункта «Запись» — экран, на который ведёт календарик из меню (`s014`).
 *
 * ⛔ Календарик закрывается ЭТИМ ключом, а не своим: своего адреса у него нет,
 * он лишь вторая дверь в журнал дня. Отдельный ключ означал бы, что дверь можно
 * закрыть, оставив комнату открытой, — а это ровно та дыра, которую запрещает
 * правило `prv3` «спрятал = закрыл».
 */
export const KLYUCH_ZHURNALA_DNYA = 'zapis-den';

/** Визуальная группа разделов (Фаза 2 редизайна — каркас .ops-skin). */
export interface NavGroup {
  label: string;
  sections: NavSection[];
}


// ─────────────────────────────────────────────────────────────────────────────
// Меню каркаса. Глобальные разделы выпадашки + аккордеон внутри — дерево
// ратифицировано владельцем 09.09.2026 (`s310`, опись `05-tasks/s310/opis-menyu.md`):
// Операционка · Аналитика · Продажи · Телефония · Реклама · Финансы ·
// AI и знания · Настройка · Сервер · Разработка. Все маршруты, права и гейты
// сохранены 1-в-1; менялись только принадлежность к разделу и подписи.
// ─────────────────────────────────────────────────────────────────────────────
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Операционка',
    sections: [
      // ⛔ `s203`: клиентская база — первым пунктом. Любой вход в систему рано
      // или поздно ведёт в клиента, и искать его владелец будет здесь, а не в
      // аналитике (там «LTV клиентов» — витрина чисел, не база).
      {
        key: 'clients-base',
        to: '/clients',
        label: 'Клиентки',
        icon: Contact,
        permission: 'customer.view',
        children: [{ key: 'clients-duplicates', to: '/clients/duplicates', label: 'Дубли', permission: 'customer.view' }, {key:'certificates',to:'/certificates',label:'Сертификаты',permission:'certificate.view'}],
      },
      // Registry label; Sidebar displays the dictionary value supplied by the server.
      {key:'master-services',to:'/master-services',label:'Мастер — услуги',icon:UserCog,permission:'services.read'},
      // ⛔ `s303`: «Запись» сразу за клиенткой — путь администратора в смене
      // начинается с дня, а не с базы людей.
      //
      // ⛔ `s014` 10.09.2026: У КАЖДОГО ЭКРАНА ЗАПИСИ ТЕПЕРЬ СВОЁ ПРАВО (владелец
      // отменил решение `s303` «экраны записи под общим админ-доступом»). Пока
      // права не было, журнал дня видели ВСЕ роли — включая редактора сайтов,
      // медсестру и внешнего директолога; слой видимости их не удерживал, потому
      // что по умолчанию он открыт, а не закрыт.
      //
      // ⛔ `neDom` остаётся: «Запись» — не стартовый экран. Права и «годится
      // домом» — разные вопросы; первый раздел без дома по-прежнему не должен
      // подменять честное «вам пока не открыт ни один раздел» (закон `prv4`).
      {
        key: 'zapis-den',
        to: '/zapis/den',
        label: 'Запись',
        icon: CalendarDays,
        permission: 'zapis.den.view',
        neDom: true,
        children: [
          /*
           * ⛔⛔ ИМЕНА ПУНКТОВ ВЕРНУЛИСЬ К МАКЕТУ (`s322`, Д43). Пункт
           * `/zapis/nezakrytoe` звался «Оплата не завершена», а соседний
           * `/zapis/bez-ishoda` — «Незакрытые записи»: слово «незакрытые» стояло
           * над ЧУЖИМ адресом, и владелец, открыв первый, написал «абсолютно не
           * понятный интерфейс». На листе 7 Астры пункты зовутся «Незакрытое» и
           * «Без исхода» — как адреса и как заголовки экранов.
           */
          { key: 'zapis-nezakrytoe', to: '/zapis/nezakrytoe', label: 'Незакрытое', permission: 'zapis.nezakrytoe.view' },
          { key: 'zapis-bez-ishoda', to: '/zapis/bez-ishoda', label: 'Без исхода', permission: 'zapis.bez_ishoda.view' },
          // ⛔ `s681`: «Доска записей» переехала к «Доске лидов» (владелец 28.09:
          // «доски записи я вообще не вижу … она вообще в другом разделе запись»).
          { key: 'zapis-ozhidanie', to: '/zapis/ozhidanie', label: 'Ожидание окна', permission: 'zapis.ozhidanie.view' },
          { key: 'zapis-roznica', to: '/zapis/roznica', label: 'Чек без записи', permission: 'zapis.roznica.view' },
          // ⛔ `s307`: режим приёмки нужен ВЛАДЕЛЬЦУ, а не роботу, и потому
          // живёт пунктом меню, а не спрятанным адресом. Пока режим выключен,
          // баннера на экранах нет — без пункта меню включить его было бы
          // неоткуда, и «переключаемый режим» существовал бы только на словах.
          // ⛔ `s014` (аудит `sec1` R-2): право владельца. Переключатель снимает
          // пять поводов отказа машины записи разом, включая гейт управляющей.
          { key: 'zapis-priyomka', to: '/zapis/priyomka', label: 'Режим приёмки', permission: 'zapis.priyomka.toggle' },
        ],
      },
      {key:'workday-shift',to:'/smena',label:'Смена',icon:CalendarDays,permission:'shift.open|shift.handover',hiddenForRoles:['master','med']},
      {key:'workday-graph',to:'/grafik',label:'График',icon:CalendarDays,permission:'schedule.view',hiddenForRoles:['master','med']},
      {key:'workday-cash',to:'/kassa-dnya',label:'Касса дня',icon:CalendarDays,permission:'cash.receive|finance.view'},
      {key:'workday-master',to:'/kabinet-mastera',label:'Мой день',icon:CalendarDays,permission:'zapis.den.view',neDom:true},
      // ⛔ Пункт «Календарь» (заведён `s309`) СНЯТ владельцем на приёмке 09.09
      // (`s310`, пункт 1): его дублирует клик по любой дате календарика, который
      // зафиксирован под выпадашкой (см. `GRUPPA_S_KALENDAREM`).
      // ⛔ `s772` (владелец 11.10.2026: «раздел лиды с форм … надо отключить, у нас
      // теперь доска лидов»): пункт «Лиды с форм» СНЯТ. Заявки с форм, недожатые формы
      // и карантин роботов живут лидами на доске; адрес `/leads/forms` ведёт на неё.
      // Зеркальная строка снята и в `RazdelyAdminki.php` (`ReestrRazdelovTest`).
      // ⛔ `s635`: доска лидов — та же очередь, разложенная колонками воронки amo.
      // Зеркальная строка — `RazdelyAdminki.php` (`doska-lidov`).
      { key: 'doska-lidov', to: '/doska-lidov', label: 'Доска лидов', icon: Columns3, permission: 'inquiry.queue.view' },
      { key: 'amo-sdelki', to: '/sdelki', label: 'Сделки', icon: Columns3, permission: 'inquiry.queue.view' },
      // ⛔ `s681`: доска записей — рядом с доской лидов, одной парой (слово владельца 28.09).
      // Зеркальная строка — `RazdelyAdminki.php` (`zapis-doska`).
      { key: 'zapis-doska', to: '/zapis/doska', label: 'Доска записей', icon: CalendarDays, permission: 'zapis.den.view' },
      // ⛔ `s681`: «Обращения» → «Переписки» (владелец 28.09: «в обращениях их,
      // наверное, проще будет переименовать в переписке»).
      { key: 'obrashcheniya', to: '/obrashcheniya', label: 'Переписки', icon: MessagesSquare, permission: 'inquiry.queue.view' },
      // ⛔ `s670`: конструктор ботов (картинки `s661`). Менеджерам скрыт слоем
      // видимости (слово владельца 24.09 «рано ещё»). Зеркало — `RazdelyAdminki.php` (`boty`).
      { key: 'visit-reviews', to: '/otzyvy', label: 'Отзывы', icon: MessagesSquare, permission: 'review.manage' },
      { key: 'boty', to: '/boty', label: 'Боты', icon: Bot, permission: 'robot.view' },
      { key: 'sms', to: '/sms', label: 'СМС', icon: MessageSquare, permission: 'sms.manage' },
    ],
  },
  {
    // `s310`: бывший «Просмотр» — витрины чисел. Имя «Аналитика» говорит,
    // что внутри, а «Просмотр» не говорил ничего.
    label: 'Аналитика',
    sections: [
      { key: 'pulse', to: '/pulse', label: 'Пульс', icon: Activity, superAdmin: true },
      // prg5 Ф4: разделы аналитики — за правами analytics.* (существующие роли
      // держат все — их вид не изменился; директолог/без-роли не видят).
      { key: 'masters', to: '/analytics/masters', label: 'Мастера', icon: Award, permission: 'analytics.masters' },
      { key: 'goods', to: '/analytics/goods', label: 'Товары', icon: ShoppingBag, permission: 'analytics.masters' },
      // ⛔ `s310` (владелец, 2а): «Клиенты / LTV» путалось с базой «Клиентки»
      // в Операционке — витрина чисел переименована, база осталась базой.
      { key: 'clients', to: '/analytics/clients', label: 'LTV клиентов', icon: Heart, permission: 'analytics.clients' },
      { key: 'analytics', to: '/analytics', label: 'Куб', icon: Boxes, permission: 'analytics.cube' },
    ],
  },
  {
    // Продажи П1 — prd2 Ф5: справочник менеджеров (галочка «отдел продаж»
    // поверх сущности сотрудника Финансов). Экраны метрик — П2, сюда же.
    label: 'Продажи',
    sections: [
      { key: 'sales-report', to: '/sales/report', label: 'Отдел продаж', icon: TrendingUp, permission: 'sales.view' },
      { key: 'sales-my-day', to: '/sales/my-day', label: 'Мой день', icon: Sun, permission: 'sales.my_day' },
      { key: 'sales-managers', to: '/sales/managers', label: 'Менеджеры', icon: Users, permission: 'sales.registry' },
      { key: 'sales-schedule', to: '/sales/schedule', label: 'График смен', icon: CalendarDays, permission: 'sales.schedule' },
    ],
  },
  {
    // Телефония (ТЗ §4.0). Навигация по ролям: менеджеру нужны ровно два
    // пункта — своё рабочее место и свои звонки. Пульт руководителя и
    // настройки линий — отдельные пункты со своими правами.
    label: 'Телефония',
    sections: [
      { key: 'tel-pult', to: '/telephony/pult', label: 'Пульт', icon: Activity, permission: 'tel.pult.view' },
      { key: 'tel-workplace', to: '/telephony/workplace', label: 'Рабочее место', icon: Headphones, permission: 'tel.workplace' },
      // `s674`: свой обзвонщик с оператором вместо СкороЗвона.
      { key: 'tel-obzvon', to: '/telephony/obzvon', label: 'Обзвон', icon: PhoneOutgoing, permission: 'tel.workplace' },
      { key: 'tel-journal', to: '/telephony/journal', label: 'Журнал звонков', icon: History, permission: 'tel.journal.view' },
      { key: 'tel-dialers', to: '/telephony/dialers', label: 'Дайлеры', icon: PhoneOutgoing, permission: 'tel.settings.view' },
      { key: 'tel-priorities', to: '/telephony/priorities', label: 'Приоритеты', icon: ArrowUpDown, permission: 'tel.settings.view' },
      { key: 'tel-import', to: '/telephony/import', label: 'Импорт Excel', icon: FileUp, permission: 'tel.settings.edit' },
      { key: 'tel-lines', to: '/telephony/lines', label: 'Линии и балансы', icon: Waypoints, permission: 'tel.settings.view' },
      { key: 'tel-scenarios', to: '/telephony/scenarios', label: 'Конструктор сценариев', icon: Workflow, permission: 'tel.settings.view' },
      { key: 'tel-settings', to: '/telephony/settings', label: 'Настройки', icon: BookOpenCheck, permission: 'tel.settings.view' },
      // ⛔ `s310`: техпроверка гарнитуры жила в «Настройке» — искать её будут
      // рядом с телефонией (tel1f: пульт оператора — отдельный трек, этот
      // пункт в него не превращается).
      { key: 'telephony-check', to: '/telephony/check', label: 'Проверка слышимости', icon: PhoneCall, permission: 'tel.pult.view' },
    ],
  },
  {
    // Реклама Р1 — служебные экраны рекламного контура. prg5 Ф4: права
    // advertising.* — super_admin держит все; роль «директолог» видит ТОЛЬКО
    // справочник/карту/ручной расход/Директ (§9.3); Яндекс — owner (секреты).
    label: 'Реклама',
    sections: [
      { key: 'ads-compass', to: '/ads', label: 'Компас · пульт', icon: Compass, permission: 'advertising.compass' },
      { key: 'ad-source-mappings', to: '/advertising/source-mappings', label: 'Справочник соответствий', icon: Megaphone, permission: 'advertising.registry' },
      { key: 'ad-status-map', to: '/advertising/status-map', label: 'Карта статусов', icon: Waypoints, permission: 'advertising.registry' },
      { key: 'ad-manual-spend', to: '/advertising/manual-spend', label: 'Ручной расход', icon: Coins, permission: 'advertising.manual_spend' },
      { key: 'ad-yandex', to: '/advertising/yandex', label: 'Интеграция Яндекса', icon: Plug, superAdmin: true },
      { key: 'ad-tracker-sites', to: '/advertising/tracker-sites', label: 'Сайты трекера', icon: Globe, permission: 'advertising.tracker' },
      { key: 'ad-tracker', to: '/advertising/tracker', label: 'Трекер', icon: Radar, permission: 'advertising.tracker' },
      { key: 'ad-direct', to: '/advertising/direct', label: 'Директ', icon: MousePointerClick, permission: 'advertising.direct' },
      { key: 'ad-vk', to: '/advertising/vk', label: 'VK Реклама', icon: MousePointerClick, permission: 'advertising.vk' },
      { key: 'ad-showcase', to: '/advertising/showcase', label: 'Витрина', icon: BarChart3, permission: 'advertising.showcase' },
      { key: 'ad-episodes', to: '/advertising/episodes', label: 'Заходы и LTV', icon: Footprints, permission: 'advertising.showcase' },
      { key: 'ad-honesty', to: '/advertising/honesty', label: 'Честность данных', icon: ShieldCheck, permission: 'advertising.showcase' },
      // ⛔ `s310` (владелец, 1а): лендинги и короткие ссылки живут под
      // рекламные кампании — из «Операций» сюда, к остальному контуру.
      {
        key: 'sites',
        to: '/sites',
        label: 'Сайты',
        icon: Globe,
        permission: 'sites.read',
        children: [{ key: 'sites-import', to: '/sites/import', label: 'Импорт', permission: 'sites.create' }],
      },
      // s962: статьи и информационные страницы сайтов (CMS) — рядом с «Сайтами».
      { key: 'content', to: '/content', label: 'Контент', icon: FileText, permission: 'content.view' },
      { key: 'links', to: '/links', label: 'Ссылки', icon: Link2, permission: 'shortener.manage' },
    ],
  },
  {
    // Финансы Ф1 — ядро модуля (источник экономической истины). Гейт —
    // permission finance.view (сидер: super_admin + admin); для остальных
    // группа целиком скрывается (пустая → null).
    label: 'Финансы',
    sections: [
      { key: 'finance-categories', to: '/finance/categories', label: 'Категории', icon: FolderTree, permission: 'finance.view' },
      { key: 'finance-templates', to: '/finance/templates', label: 'Постоянные расходы', icon: CalendarClock, permission: 'finance.view' },
      { key: 'finance-salary', to: '/finance/salary', label: 'ЗП админов', icon: Calculator, permission: 'finance.view' },
      { key: 'finance-masters-salary', to: '/finance/masters', label: 'Зарплата мастеров', icon: Calculator, permission: 'salary.master.view' },
      { key: 'finance-master-salary-rules', to: '/finance/masters/rules', label: 'Правила мастеров', icon: BookOpenCheck, permission: 'salary.master.view' },
      { key: 'finance-my-master-salary', to: '/finance/masters/my', label: 'Моя зарплата', icon: Wallet, permission: 'salary.master.self' },
      { key: 'finance-salary-rules', to: '/finance/salary/rules', label: 'Правила ЗП', icon: BookOpenCheck, permission: 'finance.view' },
      { key: 'finance-salary-prices', to: '/finance/salary/prices', label: 'Цены УС', icon: Tags, permission: 'finance.view' },
      // Своя зарплата — сотрудник видит только СВОЮ (fail-closed на API).
      { key: 'finance-my-salary', to: '/finance/my', label: 'Моя зарплата', icon: Wallet, permission: 'finance.my_salary' },
    ],
  },
  {
    // ⛔ `s310` (владелец, 3а): «Обучение» и «Костяк» — один раздел про мозг
    // системы; порознь они раздували «Операции» до 24 пунктов. Костяк
    // owner-only посекционно (вариант A К1), Обучение — за ai_memory.learn.
    label: 'AI и знания',
    sections: [
      {
        key: 'learning',
        to: '/learning',
        label: 'Обучение',
        icon: Lightbulb,
        permission: 'ai_memory.learn',
        children: [
          { key: 'learning-base', to: '/learning/base', label: 'База знаний' },
          { key: 'learning-ask', to: '/learning/ask', label: 'Спросить базу' },
          { key: 'learning-compare', to: '/learning/compare', label: 'Сравнить поиск', permission: 'emb1.compare' },
          { key: 'learning-test-run', to: '/learning/test-run', label: 'Тест-прогон' },
          { key: 'learning-staged', to: '/learning/staged', label: 'К заливке' },
          { key: 'learning-health', to: '/learning/health', label: 'Пульт базы' },
          { key: 'learning-facts', to: '/learning/facts', label: 'Факты' },
          // s042: право — то же, что у сервера (`ai_memory.observer.govern`); с `learn` пункт открывался в 403.
          { key: 'learning-observer', to: '/learning/observer', label: 'Наблюдатель', permission: 'ai_memory.observer.govern' },
        ],
      },
      { key: 'kostyak-plans', to: '/kostyak/plans', label: 'Планы', icon: Waypoints, superAdmin: true },
      { key: 'kostyak-journal', to: '/kostyak/journal', label: 'Журнал бесед', icon: MessagesSquare, superAdmin: true },
      { key: 'kostyak-control', to: '/kostyak/control', label: 'Контроль диалогов', icon: Eye, superAdmin: true },
      { key: 'kostyak-queue', to: '/kostyak/queue', label: 'Очередь улучшений', icon: Lightbulb, superAdmin: true },
      { key: 'kostyak-people', to: '/kostyak/people', label: 'Люди', icon: Users, superAdmin: true },
    ],
  },
  {
    // Проектирование ремонта помещений — 3D-модель, замеры, инженерия; личный доступ по праву.
    label: 'Ремонт',
    sections: [
      { key: 'remont', to: '/remont', label: 'Ходынка', icon: Building2, permission: 'remont.access' },
    ],
  },
  {
    label: 'NeuroFabrix',
    sections: [
      { key: 'neurofabrix-roadmap', to: '/neurofabrix/roadmap', label: 'Дорожная карта', icon: Map, superAdmin: true },
    ],
  },
  {
    // ⛔ `s310` (владелец, 09.09): бизнес-настройки, внутри поделены
    // подзаголовками по принадлежности — «сейчас каша, непонятно к чему какой
    // пункт». Системное уехало в «Сервер» ниже.
    label: 'Настройка',
    sections: [
      // ⛔ `s105` правка 6: настройки сети — ПЕРВЫМ пунктом. Это вход в
      // устройство всей системы (пороги, филиалы, блоки).
      { key: 'network', to: '/settings/network', label: 'Настройки сети', icon: Building2, permission: 'system.settings', podgruppa: 'Сеть и юрлица' },
      // `s966`: онлайн-запись (виджет) по филиалу; подпись — из словаря (`s966o.title`), Sidebar подставляет.
      { key: 'onlayn-zapis', to: '/settings/onlayn-zapis', label: 'Онлайн-запись', icon: CalendarDays, permission: 'system.settings', podgruppa: 'Сеть и юрлица' },
      { key: 'legal-entities', to: '/settings/legal-entities', label: 'Юрлица', icon: Landmark, permission: 'system.settings', podgruppa: 'Сеть и юрлица' },
      // `s675`: проекты внутри филиала — как отдельные воронки amo (сайты, номера, оффер).
      { key: 'proekty', to: '/settings/proekty', label: 'Проекты', icon: Layers, permission: 'system.settings', podgruppa: 'Сеть и юрлица' },
      {
        key: 'users',
        to: '/users',
        label: 'Пользователи',
        icon: Users,
        permission: 'users.read',
        podgruppa: 'Люди и доступ',
        children: [{ key: 'users-invite', to: '/users/invite', label: 'Приглашения', permission: 'users.invite' }],
      },
      // ⛔ `s318` (владелец, 11.09.2026): «Кто что видит» — ТРЕТИЙ слой доступа,
      // и стоит он рядом с двумя первыми. Права отвечают «что можно», роли —
      // «кем человек назначен», а этот пункт — «какие ДАННЫЕ ему показывать
      // внутри разрешённого экрана»: чьи записи и как показывать номер.
      {
        key: 'vidimost-dannyh',
        to: '/vidimost-dannyh',
        label: 'Кто что видит',
        icon: Eye,
        permission: 'vidimost.dannyh.manage',
        podgruppa: 'Люди и доступ',
      },
      // ⛔ `prv3` (владелец, 07.09.2026): пункт стоит сразу за «Пользователями»:
      // роль — это то, из чего собран человек, искать её будут рядом с людьми.
      { key: 'roles', to: '/settings/roles', label: 'Роли', icon: ShieldCheck, permission: 'roles.manage', podgruppa: 'Люди и доступ' },
      { key: 'activity', to: '/activity-log', label: 'Журнал действий', icon: History, permission: 'users.read', podgruppa: 'Люди и доступ' },
      // ⛔ `s704`: отчёт владельца о раскрытии номеров — рядом с людьми и журналом.
      // Право личное (владельцу), у ролей его нет: остальным пункт не показывается.
      { key: 'phone-reveals', to: '/staff/phone-reveals', label: 'Раскрытие телефонов', icon: PhoneCall, permission: 'staff.security.view', podgruppa: 'Люди и доступ' },
      /*
       * ⛔ `s606`: заготовки ответов — СВОЯ подгруппа «Обращения». Это не
       * «настройки сети» и не «каталог»: это слова, которыми клиника говорит с
       * клиенткой в мессенджерах, и искать их будут рядом с перепиской, а не
       * среди порогов и юрлиц.
       */
      { key: 'zagotovki', to: '/settings/zagotovki', label: 'Заготовки ответов', icon: MessageSquareText, permission: 'system.settings', podgruppa: 'Переписки' },
      // ⛔ `s310` (владелец, 4а): настройки аналитики остаются в «Настройке»
      // своей подгруппой — все настройки в одном месте.
      { key: 'baseline', to: '/analytics/baseline', label: 'Эталоны', icon: Target, permission: 'analytics.baseline', podgruppa: 'Аналитика' },
      // ⛔ `s401`: справочник процедур — НАШ каталог, а не корзины отчётов ниже.
      // Стоит перед ними намеренно: «Категории услуг» это разметка для аналитики,
      // а «Процедуры» — то, что клиника продаёт. Путать их владельцу нельзя.
      { key: 'procedury', to: '/procedury', label: 'Процедуры', icon: Layers, permission: 'services.read', podgruppa: 'Каталог' },
      { key: 'service-categories', to: '/analytics/service-categories', label: 'Категории услуг', icon: Tags, permission: 'analytics.services', podgruppa: 'Аналитика' },
      { key: 'master-visibility', to: '/analytics/master-visibility', label: 'Настройка мастеров', icon: UserCog, superAdmin: true, podgruppa: 'Аналитика' },
      { key: 'unbound', to: '/analytics/unbound-services', label: 'Непривязанные', icon: Unlink, permission: 'analytics.services', podgruppa: 'Аналитика' },
    ],
  },
  {
    // ⛔ `s310` (владелец, 09.09): «общие настройки и инфраструктура — в раздел
    // Сервер». Системный этаж: железо, интеграции, безопасность.
    label: 'Сервер',
    sections: [
      {
        key: 'infrastructure',
        to: '/infrastructure',
        label: 'Инфраструктура',
        icon: Server,
        permission: 'infrastructure.view',
        children: [
          { key: 'infrastructure-logs', to: '/infrastructure/logs', label: 'Логи', permission: 'infrastructure.view' },
          { key: 'infrastructure-queues', to: '/infrastructure/queues', label: 'Очереди', permission: 'infrastructure.view' },
          { key: 'infrastructure-services', to: '/infrastructure/services', label: 'Сервисы', permission: 'infrastructure.view' },
          { key: 'infrastructure-backups', to: '/infrastructure/backups', label: 'Бэкапы', permission: 'infrastructure.operational' },
          { key: 'infrastructure-database', to: '/infrastructure/database', label: 'База данных', permission: 'infrastructure.view' },
          { key: 'infrastructure-domains', to: '/infrastructure/domains', label: 'Домены', permission: 'infrastructure.view' },
        ],
      },
      { key: 'roadmap', to: '/roadmap', label: 'Командный центр', icon: Map, permission: 'infrastructure.view' },
      // `s737` (владелец, 10.10.2026: «в сервер запихивай»): обращения сотрудников и сбои, решения владельца.
      { key: 'support', to: '/support', label: 'Техподдержка', icon: LifeBuoy, permission: 'support.owner' },
      // ⛔ `s310`: интеграции — настройка системы, не операционная работа.
      {
        key: 'integrations',
        to: '/integrations',
        label: 'Интеграции',
        icon: Plug,
        permission: 'integrations.read',
        children: [
          // s042: право — то же, что у сервера (`AiRoutingController` — `integrations.manage`).
          { key: 'integrations-ai-routing', to: '/integrations/ai-routing', label: 'AI-маршрутизация', permission: 'integrations.manage' },
        ],
      },
      { key: 'security', to: '/settings/security', label: 'Безопасность', icon: ShieldCheck, permission: 'system.settings' },
      { key: 'access', to: '/settings/access', label: 'Вход и доступ', icon: DoorOpen, permission: 'system.settings' },
    ],
  },
  {
    // ⛔ `s105` ПРАВКА 6 — РАЗДЕЛ «РАЗРАБОТКА» ВНИЗУ.
    //
    // Экраны рабочие, но их таблицы пусты на бою: владелец, открывая такой
    // пункт, видит пустоту и не может отличить «ещё не наполнили» от
    // «сломалось». Здесь они названы вслух, а не спрятаны и не выброшены.
    // Пункт уезжает отсюда обратно в свой раздел в тот день, когда в нём
    // появляются живые строки, — и это работа ЕГО этапа.
    label: 'Разработка',
    sections: [
      { key: 'finance-operations', to: '/finance/operations', label: 'Операции (Финансы)', icon: Coins, permission: 'finance.view' },
      { key: 'audiences', to: '/analytics/audiences', label: 'Аудитории', icon: Megaphone, permission: 'analytics.audiences' },
      { key: 'sales-visibility', to: '/sales/visibility', label: 'Матрица видимости', icon: ShieldCheck, permission: 'sales.matrix' },
      { key: 'tel-pools', to: '/telephony/pools', label: 'Пулы номеров', icon: Layers, permission: 'tel.settings.view' },
    ],
  },
];

export const DEV_GROUP = 'Разработка';

export const ALL_SECTIONS: NavSection[] = NAV_GROUPS.flatMap((g) => g.sections);

/**
 * Плоский список «ключ раздела → путь» для слоя видимости (`prv3`).
 *
 * Считается ИЗ `NAV_GROUPS`, а не пишется рядом: отдельная копия списка разъехалась бы
 * с меню молча, а требование владельца — «один в один».
 */
export const RAZDELY_MENYU: { key: string; to: string }[] = NAV_GROUPS.flatMap((g) =>
  g.sections.flatMap((s) => [
    { key: s.key, to: s.to },
    ...(s.children ?? []).map((c) => ({ key: c.key, to: c.to })),
  ]),
);

/** Ключ раздела по адресу страницы — по самому длинному совпавшему пути. */
export function razdelPoAdresu(put: string): string | null {
  let luchshiy: string | null = null;
  let dlina = -1;

  for (const razdel of RAZDELY_MENYU) {
    const podhodit = put === razdel.to || put.startsWith(razdel.to + '/');
    if (podhodit && razdel.to.length > dlina) {
      luchshiy = razdel.key;
      dlina = razdel.to.length;
    }
  }

  return luchshiy;
}

/**
 * ⛔ ПЕРВЫЙ РАЗДЕЛ, КОТОРЫЙ ЧЕЛОВЕКУ ДЕЙСТВИТЕЛЬНО ОТКРЫТ (владелец, 07.09.2026, `prv3`).
 *
 * Дословно владелец: «выключил всё, кроме лидов с форм, и мне никуда не пускает теперь…
 * он видит какой-то другой стартовый экран, к которому закрыт доступ… нужно открывать
 * тот раздел, который открыт, если нет другого».
 *
 * До этого дом человека был ЗАШИТ списком («менеджер → Мой день, иначе Сайты, иначе
 * Директ, иначе Стол админа») и о слое видимости не знал вовсе: спрятали «Мой день» —
 * и менеджер приземлялся ровно на закрытый экран, а меню у него пустое. Теперь дом
 * ищется по тому же меню, что человек видит: первый раздел, который И разрешён правом,
 * И не спрятан.
 *
 * Порядок обхода — порядок меню: человек приземляется туда, куда сам бы нажал первым.
 * Ничего не открыто — возвращается `null`, и экран говорит об этом СЛОВАМИ, а не
 * гоняет по кругу редиректов.
 */
export function pervyyDostupnyyRazdel(
  can: (perm: string) => boolean,
  isSuperAdmin: boolean,
  sectionHidden: (key: string) => boolean,
): string | null {
  for (const gruppa of NAV_GROUPS) {
    for (const razdel of gruppa.sections) {
      const razdelOtkryt =
        razdelRazreshen(razdel.permission, can)
        && (!razdel.superAdmin || isSuperAdmin)
        && !sectionHidden(razdel.key)
        // Прототип виден, но домом не служит — см. `neDom` (`s303`).
        && !razdel.neDom;

      if (razdelOtkryt) {
        return razdel.to;
      }

      // Раздел закрыт — его подпункты тоже: меню рисует их только внутри него.
    }
  }

  return null;
}

/** Открыт ли этот адрес человеку — правом И видимостью. */
export function adresOtkryt(
  put: string,
  can: (perm: string) => boolean,
  isSuperAdmin: boolean,
  sectionHidden: (key: string) => boolean,
): boolean {
  const klyuch = razdelPoAdresu(put);
  if (klyuch === null) {
    // Адрес вне меню (профиль, безопасность) — слой видимости про него ничего не знает.
    return true;
  }

  if (sectionHidden(klyuch)) {
    return false;
  }

  for (const gruppa of NAV_GROUPS) {
    for (const razdel of gruppa.sections) {
      const listy = [razdel, ...(razdel.children ?? [])];
      const nash = listy.find((x) => x.key === klyuch);
      if (!nash) {
        continue;
      }

      const roditelOtkryt =
        razdelRazreshen(razdel.permission, can) && (!razdel.superAdmin || isSuperAdmin);
      const svoyoOtkryto =
        razdelRazreshen(nash.permission, can)
        && (!('superAdmin' in nash && nash.superAdmin) || isSuperAdmin);

      return roditelOtkryt && svoyoOtkryto && !sectionHidden(razdel.key);
    }
  }

  return true;
}

/**
 * s966 (сквозной прогон 25.10): у человека с обоими правами — `finance.my_salary`
 * (зарплата админа) и `salary.master.self` (зарплата мастера) — в меню стояли два
 * пункта «Моя зарплата». Пункт один: у руководства (`finance.view`) — экран админа
 * с выбором «глазами сотрудника» (мастера у него в «Зарплате мастеров»), у
 * остальных — своя ведомость мастера. Адреса обоих экранов остаются открытыми.
 */
export function dublZarplaty(key: string, can: (permission: string) => boolean): boolean {
  if (!(can('finance.my_salary') && can('salary.master.self'))) return false;
  return key === (can('finance.view') ? 'finance-my-master-salary' : 'finance-my-salary');
}

/** s878: одна проверка одиночного права или явно перечисленного OR. */
export function razdelRazreshen(permission: string | undefined, can: (permission: string) => boolean): boolean {
  return !permission || permission.split('|').some(p => p !== '' && can(p));
}
